// Nama file template diambil dari header Content-Disposition BE (mis. "uom_template.xlsx"),
// seperti yang terlihat saat tes via Apidog/Postman. Fallback 'template.xlsx' bila BE tidak
// mengirim header tersebut.
const filenameFromDisposition = (disposition, fallback = 'template.xlsx') => {
  if (!disposition) return fallback;
  // RFC 5987: filename*=UTF-8''nama.xlsx — didahulukan karena bisa berisi karakter non-ASCII
  const utf8Match = disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (utf8Match) {
    try {
      const name = decodeURIComponent(utf8Match[1].replace(/["']/g, '').trim());
      if (name) return name;
    } catch (e) { /* encoding tidak valid — lanjut ke filename biasa */ }
  }
  const match = disposition.match(/filename="?([^";]+)"?/i);
  return match ? match[1].trim() : fallback;
};

const state = {
  // Dialog visibility
  visible: false,
  
  // Loading states
  loading: false,
  templateLoading: false,
  importing: false,
  
  // File state
  file: null,
  fileName: '',
  
  // Error handling
  errors: [],        // Array of validation error messages
  errorMessage: '',  // General error message
  
  // Success state
  success: false,
  successMessage: '',
  
  // Config (per module)
  config: {
    title: 'Import Items',
    acceptTypes: '.csv,.xls,.xlsx',
    templateLabel: 'Download Template',
    importLabel: 'Import Items',
    cancelLabel: 'Cancel',
    showTemplateButton: true,
  },
  
  // Service references (set per module)
  service: null,
  templateService: null,
  importId: null,
};

const mutations = {
  SET_VISIBLE(state, visible) {
    state.visible = visible;
  },
  
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  
  SET_TEMPLATE_LOADING(state, loading) {
    state.templateLoading = loading;
  },
  
  SET_IMPORTING(state, importing) {
    state.importing = importing;
  },
  
  SET_FILE(state, { file, fileName }) {
    state.file = file;
    state.fileName = fileName || (file ? file.name : '');
  },
  
  SET_ERRORS(state, errors) {
    state.errors = Array.isArray(errors) ? errors : [];
  },
  
  SET_ERROR_MESSAGE(state, message) {
    state.errorMessage = message || '';
  },
  
  SET_SUCCESS(state, { success, message }) {
    state.success = success;
    state.successMessage = message || '';
  },
  
  SET_CONFIG(state, config) {
    state.config = { ...state.config, ...config };
  },
  
  SET_SERVICES(state, { service, templateService }) {
    state.service = service || null;
    state.templateService = templateService || null;
  },
  
  SET_IMPORT_ID(state, id) {
    state.importId = id;
  },
  
  RESET_STATE(state) {
    state.visible = false;
    state.loading = false;
    state.templateLoading = false;
    state.importing = false;
    state.file = null;
    state.fileName = '';
    state.errors = [];
    state.errorMessage = '';
    state.success = false;
    state.successMessage = '';
  },
  
  CLEAR_ERRORS(state) {
    state.errors = [];
    state.errorMessage = '';
  },
};

const actions = {
  openDialog({ commit }, config = {}) {
    if (config.config) {
      commit('SET_CONFIG', config.config);
    }
    if (config.service) {
      commit('SET_SERVICES', {
        service: config.service,
        templateService: config.templateService || null,
      });
    }
    if (config.id !== undefined) {
      commit('SET_IMPORT_ID', config.id);
    }
    commit('RESET_STATE');
    commit('SET_VISIBLE', true);
  },
  
  closeDialog({ commit }) {
    commit('RESET_STATE');
  },
  
  setFile({ commit }, file) {
    commit('SET_FILE', { file, fileName: file ? file.name : '' });
    commit('CLEAR_ERRORS');
  },
  
  clearFile({ commit }) {
    commit('SET_FILE', { file: null, fileName: '' });
    commit('CLEAR_ERRORS');
  },
  
  async downloadTemplate({ commit, state }) {
    if (!state.templateService) {
      commit('SET_ERROR_MESSAGE', 'Template service not configured');
      return false;
    }
    
    commit('SET_TEMPLATE_LOADING', true);
    try {
      const response = await state.templateService();
      const blob = response instanceof Blob ? response : (response?.data instanceof Blob ? response.data : new Blob([response?.data || '']));
      const filename = filenameFromDisposition(response?.headers?.['content-disposition']);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      commit('SET_ERROR_MESSAGE', error?.response?.data?.message || 'Failed to download template');
      return false;
    } finally {
      commit('SET_TEMPLATE_LOADING', false);
    }
  },
  
  async importFile({ commit, state }, { id, serviceFn }) {
    if (!state.file) {
      commit('SET_ERRORS', [{ row: null, field: null, value: null, message: 'Please select a file to import' }]);
      return false;
    }
    
    // Use id from action param, or fallback to state.importId
    const resolvedId = id || state.importId;
    
    commit('SET_IMPORTING', true);
    commit('CLEAR_ERRORS');
    commit('SET_SUCCESS', { success: false, message: '' });
    
    try {
      const service = serviceFn || state.service;
      if (!service) {
        throw new Error('Import service not configured');
      }
      
      const response = await service(resolvedId, state.file);
      
      // Check if response indicates validation errors
      const responseData = response?.data || response;
      
      if (responseData?.status === 'error') {
        // Handle validation error response
        // data can be: array of objects [{row, field, value, error}] or array of strings
        const errors = responseData.data || [];
        if (Array.isArray(errors)) {
          // Normalize: if items are objects with 'error' key, keep as-is; if strings, wrap
          const normalized = errors.map(e => {
            if (e && typeof e === 'object' && e.error) {
              return { row: e.row, field: e.field, value: e.value, message: e.error };
            }
            return { row: null, field: null, value: null, message: String(e) };
          });
          commit('SET_ERRORS', normalized);
        } else {
          commit('SET_ERRORS', [{ row: null, field: null, value: null, message: responseData.message || 'Validation failed' }]);
        }
        return false;
      }
      
      // Success
      commit('SET_SUCCESS', {
        success: true,
        message: responseData?.message || 'Import completed successfully',
      });
      commit('SET_FILE', { file: null, fileName: '' });
      return true;
    } catch (error) {
      // Handle API error response with validation errors
      const errorData = error?.response?.data;
      
      if (errorData?.status === 'error' && Array.isArray(errorData.data)) {
        const normalized = errorData.data.map(e => {
          if (e && typeof e === 'object' && e.error) {
            return { row: e.row, field: e.field, value: e.value, message: e.error };
          }
          return { row: null, field: null, value: null, message: String(e) };
        });
        commit('SET_ERRORS', normalized);
      } else if (errorData?.message) {
        commit('SET_ERROR_MESSAGE', errorData.message);
      } else if (error?.message) {
        commit('SET_ERROR_MESSAGE', error.message);
      } else {
        commit('SET_ERROR_MESSAGE', 'Failed to import file');
      }
      return false;
    } finally {
      commit('SET_IMPORTING', false);
    }
  },
  
  // Parse validation error response format
  parseValidationErrors(response) {
    if (!response) return [];
    
    const data = response?.data || response;
    
    if (data?.status === 'error' && Array.isArray(data.data)) {
      return data.data;
    }
    
    return [];
  },
};

const getters = {
  hasErrors: (state) => state.errors.length > 0 || state.errorMessage !== '',
  errorCount: (state) => state.errors.length + (state.errorMessage ? 1 : 0),
  allErrors: (state) => {
    const errors = [...state.errors];
    if (state.errorMessage) {
      errors.unshift({ row: null, field: null, value: null, message: state.errorMessage });
    }
    return errors;
  },
  canImport: (state) => state.file !== null && !state.importing,
  isReady: (state) => !state.loading && !state.importing,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
